/**
 * Bulk invoice CSV helpers.
 * Columns: email,fname,lname,street,address2,city,state,zip,referenceId,amount
 * address2 is optional; all other fields are required.
 */

export const BULK_CSV_HEADERS = [
  "email",
  "fname",
  "lname",
  "street",
  "address2",
  "city",
  "state",
  "zip",
  "referenceId",
  "amount",
];

export const BULK_CSV_SAMPLE_PATH = "/bulk-payments-sample.csv";

const REF_ALLOWED = /^[A-Za-z0-9_-]+$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STATE_RE = /^[A-Za-z]{2}$/;
const ZIP_RE = /^\d{5}(-\d{4})?$/;

function stripBom(text) {
  return String(text || "").replace(/^\uFEFF/, "");
}

/** Minimal CSV line parser (supports quoted fields). */
function parseCsvLine(line) {
  const cells = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      cells.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  cells.push(cur);
  return cells.map((c) => c.trim());
}

function normalizeHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

/**
 * @returns {{ rows: Array<object>, error?: string }}
 */
export function parseBulkInvoiceCsv(text) {
  const raw = stripBom(text);
  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 2) {
    return { rows: [], error: "CSV must include a header row and at least one data row." };
  }

  const headers = parseCsvLine(lines[0]).map(normalizeHeader);
  const missing = BULK_CSV_HEADERS.filter(
    (h) => !headers.includes(normalizeHeader(h))
  );
  if (missing.length) {
    return {
      rows: [],
      error: `Missing required column(s): ${missing.join(", ")}`,
    };
  }

  const index = {};
  BULK_CSV_HEADERS.forEach((h) => {
    index[h] = headers.indexOf(normalizeHeader(h));
  });

  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cells = parseCsvLine(lines[i]);
    const get = (key) => String(cells[index[key]] ?? "").trim();
    rows.push({
      rowNumber: i + 1,
      email: get("email"),
      fname: get("fname"),
      lname: get("lname"),
      street: get("street"),
      address2: get("address2"),
      city: get("city"),
      state: get("state").toUpperCase(),
      zip: get("zip"),
      referenceId: get("referenceId"),
      amount: get("amount"),
    });
  }
  return { rows };
}

/**
 * @returns {{ valid: boolean, errors: string[], amountDollars?: number }}
 */
export function validateBulkInvoiceRow(row) {
  const errors = [];
  if (!row.email) errors.push("email is required");
  else if (!EMAIL_RE.test(row.email)) errors.push("email is invalid");

  if (!row.fname) errors.push("fname is required");
  if (!row.lname) errors.push("lname is required");
  if (!row.street) errors.push("street is required");
  if (!row.city) errors.push("city is required");

  if (!row.state) errors.push("state is required");
  else if (!STATE_RE.test(row.state)) errors.push("state must be 2 letters");

  if (!row.zip) errors.push("zip is required");
  else if (!ZIP_RE.test(row.zip)) errors.push("zip must be 5 or 9 digits");

  if (!row.referenceId) errors.push("referenceId is required");
  else if (row.referenceId.length > 128) errors.push("referenceId too long");
  else if (!REF_ALLOWED.test(row.referenceId)) {
    errors.push("referenceId has invalid characters");
  }

  let amountDollars;
  if (!row.amount) {
    errors.push("amount is required");
  } else {
    const n = Number(String(row.amount).replace(/[$,]/g, ""));
    if (!Number.isFinite(n) || n <= 0) errors.push("amount must be a positive number");
    else amountDollars = n;
  }

  return { valid: errors.length === 0, errors, amountDollars };
}

export function validateBulkInvoiceRows(rows) {
  return rows.map((row) => {
    const result = validateBulkInvoiceRow(row);
    return {
      ...row,
      valid: result.valid,
      errors: result.errors,
      amountDollars: result.amountDollars,
      sendStatus: "idle", // idle | sending | sent | failed
      sendError: "",
    };
  });
}
